<template>
    <div>
        <!-- Loader Component -->
        <Loader :visible="isLoading" :text="loadingText" />


        <ConfirmationModal :show="confirmDeleteShow" @close="confirmDeleteShow = false">
            <template #title>Conferma Eliminazione Contratto</template>

            <template #content>
                Sei sicuro di voler eliminare il contratto {{ contractDeletion?.DocNo }} di {{ contractDeletion?.CompanyName }}? Questa operazione non è reversibile.
            </template>

            <template #footer>
                <button 
                    @click="confirmDeleteShow = false" 
                    class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                >
                    No
                </button>
                <button 
                    @click="deleteContract()" 
                    class="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                >
                    Sì
                </button>
            </template>
        </ConfirmationModal>

        <!-- Contract Details Modal -->
        <div v-if="showDetailsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
                <div class="flex justify-between items-center p-6 border-b">
                    <h3 class="text-xl font-semibold">Dettagli Contratto {{ selectedContract?.DocNo }}</h3>
                    <button 
                        @click="closeDetailsModal" 
                        class="text-gray-400 hover:text-gray-600"
                    >
                        <i class="fa fa-times text-xl"></i>
                    </button>
                </div>
                
                <div class="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    <div v-if="loadingDetails" class="text-center py-8">
                        <i class="fa fa-spinner fa-spin text-2xl text-blue-500"></i>
                        <p class="mt-2 text-gray-600">Caricamento dettagli in corso...</p>
                    </div>
                    
                    <div v-else-if="selectedContract">
                        <!-- Contract Info -->
                        <div class="bg-gray-50 rounded-lg p-4 mb-6">
                            <h4 class="font-semibold text-lg mb-3">Informazioni Contratto</h4>
                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <span class="text-sm text-gray-600">Codice:</span>
                                    <p class="font-medium">{{ selectedContract.DocNo }}</p>
                                </div>
                                <div>
                                    <span class="text-sm text-gray-600">Cliente:</span>
                                    <p class="font-medium">{{ selectedContract.CompanyName }}</p>
                                </div>
                                <div>
                                    <span class="text-sm text-gray-600">Tipo Contratto:</span>
                                    <span class="px-2 py-1 text-xs rounded-full font-medium"
                                        :class="getContractTypeClass(selectedContract.ContractType)">
                                        {{ getContractTypeLabel(selectedContract.ContractType) }}
                                    </span>
                                </div>
                                <div>
                                    <span class="text-sm text-gray-600">Data Stipula:</span>
                                    <p class="font-medium">{{ formatDate(selectedContract.StipulationDate) }}</p>
                                </div>
                                <div>
                                    <span class="text-sm text-gray-600">Ultima Revisione:</span>
                                    <p class="font-medium">{{ formatDate(selectedContract.LastRevision) }}</p>
                                </div>
                                <div v-if="selectedContract.Model">
                                    <span class="text-sm text-gray-600">Modello:</span>
                                    <p class="font-medium">{{ selectedContract.Model }}</p>
                                </div>
                            </div>
                            <div v-if="selectedContract.Notes" class="mt-4">
                                <span class="text-sm text-gray-600">Note:</span>
                                <p class="font-medium whitespace-pre-line">{{ selectedContract.Notes }}</p>
                            </div>
                        </div>

                        <!-- Contract Lines -->
                        <div v-if="selectedContract.contractLines && selectedContract.contractLines.length > 0">
                            <h4 class="font-semibold text-lg mb-3">Righe Contratto</h4>
                            <div class="overflow-x-auto">
                                <table class="min-w-full text-sm border border-gray-200">
                                    <thead class="bg-gray-100">
                                        <tr class="bg-gray-100">
                                        <th class="py-2 px-4 border-b text-left">Codice</th>
                                        <th class="py-2 px-4 border-b text-left">Descrizione</th>
                                        <th class="py-2 px-4 border-b text-center">Data inizio</th>
                                        <th class="py-2 px-4 border-b text-center">Data fine</th>
                                        <th class="py-2 px-4 border-b text-center">UM</th>
                                        <th class="py-2 px-4 border-b text-center">Qtà</th>
                                        <th class="py-2 px-4 border-b text-center">Valore unitario</th>
                                        <th class="py-2 px-4 border-b text-center">Totale</th>
                                        <th class="py-2 px-4 border-b text-center">Attivo</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="line in selectedContract.contractLines" :key="line.RowId" class="hover:bg-gray-50">
                                            <td class="py-2 px-4 border-b">{{ line.Item }}</td>
                                            <td class="py-2 px-4 border-b">{{ line.Description }}</td>
                                            <td class="py-2 px-4 border-b text-center">{{ (line.StartDate) ? formatDate(line.StartDate) : '-' }}</td>
                                            <td class="py-2 px-4 border-b text-center">{{ (line.EndDate) ? formatDate(line.EndDate) : '-' }}</td>
                                            <td class="py-2 px-4 border-b text-center">{{ line.UoM }}</td>
                                            <td class="py-2 px-4 border-b text-center">{{ formatNumber(line.Qty) }}</td>
                                            <td class="py-2 px-4 border-b text-right">{{ formatCurrency(line.UnitValue, 3) }}</td>
                                            <td class="py-2 px-4 border-b text-right">{{ formatCurrency(line.TotalAmount, 3) }}</td>
                                            <td class="py-2 px-4 border-b text-center">
                                            <span v-if="line.Disabled == '1'" class="text-red-600">X</span>
                                            <span v-else class="text-green-600">✓</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        <div v-else class="text-center py-8 text-gray-500">
                            <i class="fa fa-inbox text-3xl mb-2"></i>
                            <p>Nessuna riga trovata per questo contratto</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>
        
        <div class="p-6">
            <!-- Sezione di ricerca -->
            <div class="mb-4 bg-white rounded-md shadow-lg">
                <div class="px-6 py-3 bg-gray-100 rounded-t-md">
                    <h3 class="font-semibold">Ricerca Contratto</h3>
                </div>

                <div class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Codice contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Codice contratto</label>
                            <input 
                                v-model="filters.contractCode" 
                                type="text" 
                                class="p93__input w-full"
                                placeholder="Inserisci codice contratto" 
                                @keyup.enter="doSearch"
                            />
                            <div v-if="errors.contractCode"
                                class="mt-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                                {{ errors.contractCode }}
                            </div>
                        </div>

                        <!-- Modello contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Modello</label>
                            <v-select 
                                :options="contractModels"
                                v-model="filters.model"
                                label="name"
                                placeholder="Seleziona modello contratto..." 
                                class="w-full"
                            >
                            </v-select>
                        </div>

                        <!-- Tipo contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Tipo contratto</label>
                            <v-select 
                                :options="contractTypes"
                                v-model="filters.contractType"
                                label="name"
                                placeholder="Seleziona tipo contratto..." 
                                class="w-full"
                            >
                            </v-select>
                        </div>
                        

                        <!-- Cliente -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Cliente</label>
                            <v-select 
                                :options="foundClients" 
                                :filterable="false" 
                                v-model="filters.customer"
                                label="name" 
                                @search="searchClients" 
                                placeholder="Cerca cliente..." 
                                class="w-full"
                            >
                                <template #no-options="{ search, searching }">
                                    <div v-if="searching">🔍 Ricerca in corso...</div>
                                    <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
                                </template>
                            </v-select>
                            <div v-if="errors.customer"
                                class="mt-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                                {{ errors.customer }}
                            </div>
                        </div>

                        <!-- Data Stipula -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Intervallo di date stipula</label>
                            <VueDatePicker v-model="filters.stipulationDateRange" range locale="it" format="dd/MM/yyyy" auto-apply
                                placeholder="Seleziona intervallo date" :enable-time-picker="false" text-input
                                class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInputRange"/>
                        </div>

                        <!-- Data Stipula -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Intervallo di date ultima revisione</label>
                            <VueDatePicker v-model="filters.lastRevisionDateRange" range locale="it" format="dd/MM/yyyy" auto-apply
                                placeholder="Seleziona intervallo date" :enable-time-picker="false" text-input
                                class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInputRange"/>
                        </div>

                        
                    </div>

                    <!-- Bottoni cerca e reset -->
                    <div class="flex justify-end mt-4 space-x-2">
                        <button 
                            @click="resetFilters"
                            class="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
                            :disabled="processing"
                        >
                            <i class="fa fa-undo mr-2"></i>
                            Reset
                        </button>
                        <button 
                            @click="doSearch"
                            class="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600"
                            :disabled="processing"
                        >
                            <i class="fa fa-search mr-2"></i>
                            {{ processing ? 'Ricerca in corso...' : 'Cerca' }}
                        </button>
                    </div>

                    <!-- Messaggio di errore generale -->
                    <div v-if="errors.filters || errors.search"
                        class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                        <i class="fa fa-exclamation-triangle mr-2"></i>
                        {{ errors.filters || errors.search }}
                    </div>
                </div>
            </div>

            <!-- Risultati ricerca -->
            <div v-if="searchPerformed" class="bg-white rounded-md shadow-lg">
                <div class="flex justify-between items-center p-4 border-b">
                    <h3 class="text-lg font-semibold">Risultati Ricerca</h3>
                </div>

                <div class="p-4">
                    <PageServerTable
                        :headers="headers"
                        :items="contracts"
                        :items-per-page.sync="ipp"
                        :items-per-page-options="ippo"
                        :sort-by="sortby"
                        :items-length="total"
                        :reload-fn="searchContract"
                        item-value="ContractId"
                        :page="page"
                        show-current-page
                    >
                        <template #item.ContractType="{ item }">
                            <span class="px-2 py-1 text-xs rounded-full"
                                :class="getContractTypeClass(item.ContractType)">
                                {{ getContractTypeLabel(item.ContractType) }}
                            </span>
                        </template>
                        <template #item.Model="{ item }">
                            {{ item.Model || '-' }}
                        </template>
                        <template #item.StipulationDate="{ item }">
                            {{ formatDate(item.StipulationDate) }}
                        </template>
                        <template #item.NearestExpiry="{ item }">
                            {{ item.NearestExpiry ? formatDate(item.NearestExpiry) : '-' }}
                        </template>
                        <template #item.Disabled="{ item }">
                            <span v-if="item.Disabled == '1'" class="text-red-600">X</span>
                            <span v-else class="text-green-600">✓</span>
                        </template>
                        <template #item.actions="{ item }">
                            <div class="flex justify-center space-x-2">
                                <button 
                                    @click="viewContractDetails(item.ContractId)"
                                    class="bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600 text-sm"
                                    title="Visualizza dettagli"
                                ><i class="fa fa-eye mr-1"></i></button>
                                <button 
                                    @click="editContract(item.ContractId)"
                                    class="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 text-sm"
                                ><i class="fa fa-edit mr-1"></i></button>
                                <button 
                                    @click="contractDeletion = item; confirmDeleteShow=true"
                                    class="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 text-sm"
                                ><i class="fa fa-trash mr-1"></i></button>
                            </div>
                        </template>
                    </PageServerTable>
                </div>
            </div>

            <!-- Istruzioni per l'utente (mostra solo se non è stata fatta ricerca) -->
            <div v-if="!searchPerformed" class="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div class="flex items-start">
                    <i class="fa fa-info-circle text-blue-500 mt-1 mr-3"></i>
                    <div>
                        <h4 class="text-blue-800 font-medium mb-1">Come utilizzare la ricerca</h4>
                        <ul class="text-blue-700 text-sm space-y-1">
                            <li>• Inserisci il <strong>codice contratto</strong> per cercare un contratto specifico</li>
                            <li>• Altrimenti seleziona un <strong>cliente</strong> per trovare tutti i suoi contratti</li>
                            <li>• Oppure <strong>modello</strong> o <strong>tipo</strong> contratto, o un <strong>range</strong> di date.</li>
                            <li>• È necessario compilare almeno uno dei campi presenti</li>
                            <li>• Clicca su <strong><i class="fa fa-edit mr-1"></i></strong> nella tabella risultati per modificare il contratto in una nuova pagina.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <ToastNotification
            :show="toast.show"
            :message="toast.message"
            :type="toast.type"
            @update:show="toast.show = $event"
        />
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import { useLoader } from '@/Composables/useLoader.js';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import ToastNotification from '@/Components/ToastNotification.vue';

export default {
    props: ['contractModels', 'contractModelCode'],
    components: {
        Link,
        BreadCrumbs,
        Loader,
        ToastNotification,
        ConfirmationModal,
        PageServerTable
    },
    setup() {
        const { isLoading, loadingText, showLoader, hideLoader, withLoader, withLoaderSafe, withInertiaLoader } = useLoader();

        return {
            isLoading,
            loadingText,
            showLoader,
            hideLoader,
            withLoader,
            withLoaderSafe,
            withInertiaLoader
        };
    },
    data() {
        return {
            foundClients: [],
            filters: {
                contractCode: '',
                customer: null,
                model: null,
                contractType: null,
                stipulationDateRange: null,
                lastRevisionDateRange: null
            },
            errors: {},
            processing: false,
            contracts: [],
            total: 0,
            searchPerformed: false,
            headers: [
                { title: 'Codice Contratto', align: 'start', key: 'DocNo' },
                { title: 'Cliente', align: 'start', key: 'CompanyName' },
                { title: 'Tipo Contratto', align: 'start', key: 'ContractType', sortable: false },
                { title: 'Modello', align: 'start', key: 'Model' },
                { title: 'Data stipula', align: 'start', key: 'StipulationDate' },
                { title: 'Scadenza', align: 'start', key: 'NearestExpiry' },
                { title: 'Attivo', align: 'center', key: 'Disabled', sortable: false, filterable: true, boolean: true, filtervalue: true },
                { title: 'Azioni', align: 'center', key: 'actions', sortable: false }
            ],
            sortby: [{ key: 'DocNo', order: 'desc' }],
            ipp: 10,
            ippo: [
                { value: 10, title: '10' },
                { value: 25, title: '25' },
                { value: 50, title: '50' },
                { value: 100, title: '100' }
            ],
            page: 1,
            navigation: [
                { name: 'Dashboard', href: route('dashboard') },
                { name: 'Contratti', href: route('modules.home', 'contratti') },
                { name: 'Ricerca Contratti', href: route('modules.contratti.ricerca') }
            ],
            contractTypes: [
                { value: 'SER', name: 'Servizi' },
                { value: 'NOL', name: 'Noleggio' },
                { value: 'PCO', name: 'Pacchetto Ore' }
            ],
            showMessageModal: false,
            contractDeletion: null,
            confirmDeleteShow: false,
            messageContent: '',
            messageType: 'info',
            showDetailsModal: false,
            selectedContract: null,
            loadingDetails: false,
            toast: {
                show: false,
                message: '',
                type: 'success'
            }
        };
    },
    methods: {
        searchClients(search, loading) {
            if (search.length >= 2) {
                loading(true);
                clearTimeout(this.searchContactsTimeout);
                this.searchContactsTimeout = setTimeout(() => {
                    axios
                        .get(route('modules.contratti.ricerca-clienti', search))
                        .then((response) => {
                            this.foundClients = response.data;
                            loading(false);
                        });
                }, 300);
            }
        },
        showToast(message, type = 'success') {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
        },
        doSearch() {
            this.page = 1;
            this.searchContract();
        },
        async searchContract(sortby = undefined, itemsperpage = undefined, page = undefined) {
            if (sortby) this.sortby = sortby;
            if (itemsperpage) this.ipp = itemsperpage;
            if (page) this.page = page;

            this.errors = {};
            this.processing = true;

            const payload = {
                contractCode: this.filters.contractCode || null,
                customer: this.filters.customer?.id || null,
                model: this.filters.model?.id || null,
                contractType: this.filters.contractType?.value || null,
                stipulationDateRange: this.filters.stipulationDateRange ? {
                    start: format(this.filters.stipulationDateRange[0], 'yyyy-MM-dd'),
                    end: format(this.filters.stipulationDateRange[1], 'yyyy-MM-dd')
                } : null,
                lastRevisionDateRange: this.filters.lastRevisionDateRange ? {
                    start: format(this.filters.lastRevisionDateRange[0], 'yyyy-MM-dd'),
                    end: format(this.filters.lastRevisionDateRange[1], 'yyyy-MM-dd')
                } : null,
                headers: this.headers,
                sortby: this.sortby,
                itemsperpage: this.ipp,
                page: this.page
            };

            try {
                const response = await axios.post(route('modules.contratti.cerca-contratti'), payload);
                this.contracts = response.data.contracts;
                this.total = response.data.total;
                this.sortby = response.data.sortby;
                this.ipp = response.data.ipp;
                this.page = response.data.page;
                this.searchPerformed = true;
            } catch (error) {
                if (error.response && error.response.status === 422) {
                    const responseErrors = error.response.data.errors || {};
                    const flatErrors = {};
                    Object.keys(responseErrors).forEach((key) => {
                        flatErrors[key] = Array.isArray(responseErrors[key]) ? responseErrors[key][0] : responseErrors[key];
                    });
                    this.errors = flatErrors;
                } else {
                    console.error('Errore durante la ricerca dei contratti:', error);
                    this.showToast('Errore durante la ricerca dei contratti', 'error');
                }
            } finally {
                this.processing = false;
            }
        },
        resetFilters() {
            // Reset all filters to their initial state
            this.filters = {
                contractCode: '',
                customer: null,
                model: null,
                contractType: null,
                stipulationDateRange: null,
                lastRevisionDateRange: null
            };

            this.errors = {};
            this.foundClients = [];
            this.contracts = [];
            this.total = 0;
            this.searchPerformed = false;
            const disabledHeader = this.headers.find(h => h.key === 'Disabled');
            if (disabledHeader) disabledHeader.filtervalue = true;
            this.page = 1;
            this.sortby = [{ key: 'DocNo', order: 'desc' }];
        },
        editContract(contractId) {
            // Redirect alla pagina di modifica del contratto
            window.open(route('modules.contratti.esistente', contractId), '_blank');
        },
        async viewContractDetails(contractId) {
            this.showDetailsModal = true;
            this.loadingDetails = true;
            this.selectedContract = null;

            try {
                const response = await axios.get(route('modules.contratti.dettagli', contractId));
                this.selectedContract = response.data;
            } catch (error) {
                console.error('Errore nel caricamento dei dettagli:', error);
                this.showToast('Errore nel caricamento dei dettagli del contratto', 'error');
                this.showDetailsModal = false;
            } finally {
                this.loadingDetails = false;
            }
        },
        closeDetailsModal() {
            this.showDetailsModal = false;
            this.selectedContract = null;
            this.loadingDetails = false;
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            try {
                return format(parseISO(dateString), 'dd/MM/yyyy');
            } catch (e) {
                return dateString;
            }
        },
        getContractTypeClass(type) {
            switch (type) {
                case 'SER':
                    return 'bg-blue-100 text-blue-800';
                case 'NOL':
                    return 'bg-green-100 text-green-800';
                case 'PCO':
                    return 'bg-purple-100 text-purple-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        },
        getContractTypeLabel(type) {
            switch (type) {
                case 'SER':
                    return 'Servizi';
                case 'NOL':
                    return 'Noleggio';
                case 'PCO':
                    return 'Pacchetto Ore';
                default:
                    return type;
            }
        },
        async deleteContract() {
            await this.withLoaderSafe(
                async () => {
                    await axios.post(route('modules.contratti.elimina'), {
                        ContractId: this.contractDeletion.ContractId
                    }).then((response) => {
                        if (response.data.success){
                            this.showToast('Contratto eliminato con successo', 'success');
                            this.confirmDeleteShow = false;

                            // Ricarica la pagina dei contratti dopo l'eliminazione
                            this.searchContract();
                        } else {
                            this.showToast(response.data.message || 'Errore durante l\'eliminazione del contratto', 'error');
                        }
                    });                
                },
                `Eliminazione in corso di ${this.contractDeletion.DocNo}...`,
                (error) => {
                    console.error('Errore durante l\'eliminazione:', error);
                    this.showToast('Errore durante l\'eliminazione del contratto', 'error');
                }
            );
        },
    },
    mounted() {
        if (this.contractModelCode) {
            const matchedModel = this.contractModels?.find(m => m.id == this.contractModelCode);
            this.filters.model = matchedModel || { id: this.contractModelCode, name: this.contractModelCode };
            this.doSearch();
        }
    }
};
</script>
